import createCollectionResource from 'src/actions/shared/createResource';
import { AppThunk } from 'src/actions/shared/sharedTypes';
import { Communicator } from 'src/models/Communicator';
import generateId from 'src/lib/generateId';
import { toastError, toastSuccess } from 'src/actions/toast';

export enum AccountType {
  FacebookMessenger = 'FB_MESSENGER',
  Instagram = 'INSTAGRAM',
  InstagramDirect = 'INSTAGRAM_DIRECT',
  ChatRocket = 'CHAT_ROCKET',
}

export interface FacebookAccountContent {
  id: string;
  instagramBusinessAccount?: InstagramBusinessAccount;
  isTemporaryToken: Boolean;
  name: string;
}

export interface InstagramBusinessAccount {
  id: string;
}

export interface FacebookSecretContent {
  authToken: string;
}

// Should match AD model src/scripts/domain/models/account.js
export interface Account {
  id: string;
  type: AccountType;
  content: FacebookAccountContent;
  createdAt?: string;
  createdBy?: Communicator;
  description: string; // unique key constraint applies to this property
  secretContent: FacebookSecretContent;
}

export function createFacebookAccount(pageId: string, authToken: string, name: string): Account {
  return {
    id: generateId(),
    description: `${name} (${pageId})`,
    type: AccountType.FacebookMessenger,
    content: {
      id: pageId,
      isTemporaryToken: true,
      name,
    },
    secretContent: {
      authToken,
    },
  };
}

export function createInstagramAccount(
  pageId: string,
  authToken: string,
  name: string,
  instagramBusinessAccount: InstagramBusinessAccount | undefined,
  accountType: AccountType.Instagram | AccountType.InstagramDirect = AccountType.Instagram
): Account {
  return {
    id: generateId(),
    description: `Instagram ${name} (${pageId})`,
    type: accountType,
    content: {
      id: pageId,
      isTemporaryToken: true,
      name,
      instagramBusinessAccount,
    },
    secretContent: {
      authToken,
    },
  };
}

const removeAccount =
  ({ id, errorMessage, successMessage }: { id: string; errorMessage?: string; successMessage?: string }) =>
  async dispatch => {
    const errMsg = 'Unable to delete account from Gladly. Please try again.';
    const { deleteStarted, deleteSuccess } = accountsResource.actions;
    try {
      dispatch(deleteStarted({}));
      await dispatch(
        accountsResource.effects.remove(
          { accountId: id },
          { customToastMsg: successMessage, customToastErrorMsg: errorMessage }
        )
      );
      dispatch(deleteSuccess({}));
    } catch (error) {
      dispatch(toastError(errMsg));
    }
  };

const updateAccount: AppThunk =
  (params: { orgId: string; accountId: string }, attrs: { authToken: string }, opts?: {}) =>
  async (dispatch, getState, { gateways }) => {
    const { updateStarted, updateSuccess } = accountsResource.actions;
    try {
      dispatch(updateStarted({}));
      await gateways.accounts.update(params, attrs, opts);
      dispatch(toastSuccess('Successfully updated Instagram Account.'));
      dispatch(updateSuccess({}));
    } catch (error) {
      dispatch(toastError('There was an error while updating the Instagram account'));
    }
  };

const accountsResource = createCollectionResource<Account>({
  name: 'accounts',
  endpoint: {
    addUrl: '/api/v1/orgs/:orgId/configuration/accounts',
    broadcastDeleteTopic: 'v1/orgs/:orgId/configuration/accounts/:accountId/event/delete',
    broadcastTopic: 'v1/orgs/:orgId/configuration/accounts/:accountId',
    collectionBroadcastDeleteTopic: 'v1/orgs/:orgId/configuration/accounts/+/event/delete',
    collectionBroadcastTopic: 'v1/orgs/:orgId/configuration/accounts/+',
    deleteUrl: '/api/v1/orgs/:orgId/configuration/accounts/:accountId',
    fetchAllUrl: '/api/v1/orgs/:orgId/configuration/accounts',
    fetchUrl: '/api/v1/orgs/:orgId/configuration/accounts/:accountId',
    replaceUrl: '/api/v1/orgs/:orgId/configuration/accounts/:accountId',
    updateUrl: '/api/facebook/v1/orgs/:orgId/accounts/:accountId', // Custom Facebook endpoint for PATCH
  },
  options: {
    patchOnUpdate: true, // Use PATCH by default for updates
    toastMessages: {
      onUpdate: () => `Successfully updated Instagram Account.`,
      onRemove: () => `The account has been deleted from Gladly.`,
    },
  },
  observerCreator: ({ actions }) => {
    return ({ dispatch }) => {
      return {
        onBroadcast(item) {
          dispatch(actions.broadcast(item));
        },
        onBroadcastDelete(_: any, params) {
          dispatch(actions.broadcastDelete(params.accountId));
        },
      };
    };
  },
});

accountsResource.effects.removeAccount = removeAccount;
accountsResource.effects.update = updateAccount;
export default accountsResource;
